import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { connect, useSelector } from "react-redux";
import { multilanguage } from "redux-multilanguage";
import { wfetch } from "../../../utils/fetch";
import { getApiEndpoint } from "../../../utils/constants/api";
import { decodeText } from "../../../utils/richText";
import { childNodes, flag, splitLines } from "./utils";
import Logo from "./sub-components/Logo";
import NavMenu from "./NavMenu";
import IconGroup from "./IconGroup";
import PromoBar from "./PromoBar";
import TrustBar from "./TrustBar";
import SearchBox from "./SearchBox";
import MobileOffCanvas from "./MobileOffCanvas";
import LanguageCurrencyChanger from "./sub-components/LanguageCurrencyChanger";

// Same breakpoint as _shop-header.scss: below it the header is the compact mobile variant.
const DESKTOP_QUERY = "(min-width: 800px)";

// Storefront header (SERVDEMO-734), modelled on bergzeit.de: dismissible promo bar, trust bar,
// a header row with logo, search and icons, and the navigation links below it. Once promo and
// trust bar have scrolled away the header row sticks to the top in a condensed form (smaller
// logo, no icon labels, navigation hidden behind a burger); on small screens the burger opens
// the off-canvas menu instead. Everything authorable comes from the main navigation node.
const Header = ({ currentLanguageCode, navigationId }) => {
    const [content, setContent] = useState({});
    const [condensed, setCondensed] = useState(false);
    const [navOpen, setNavOpen] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [barHeight, setBarHeight] = useState(0);
    const sentinelRef = useRef(null);
    const barRef = useRef(null);
    const location = useLocation();
    const rootPage = useSelector((state) => state.rootPageData) || {};

    useEffect(() => {
        async function fetchData() {
            const response = await wfetch(
                getApiEndpoint("NAVIGATION", {
                    queryParams: { "@jcr:uuid": navigationId, lang: currentLanguageCode },
                }),
            );

            if (response && response.data.results[0]) setContent(response.data.results[0]);
        }

        if (navigationId) fetchData();
    }, [currentLanguageCode, navigationId]);

    // A sentinel sits right above the header row. Once it has scrolled past the top edge the
    // row switches to fixed positioning (condensed); a placeholder of the row's normal height
    // keeps the page from jumping. Scrolling back up resets the desktop burger state.
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel || typeof IntersectionObserver === "undefined") return undefined;

        const observer = new IntersectionObserver(([entry]) => {
            const stuck = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            setCondensed(stuck);
            if (!stuck) setNavOpen(false);
        });
        observer.observe(sentinel);

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const measure = () => {
            if (barRef.current && !condensed) setBarHeight(barRef.current.offsetHeight);
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [condensed, content]);

    // A route change closes whatever menu is open.
    useEffect(() => {
        setNavOpen(false);
        setMobileOpen(false);
    }, [location.pathname]);

    const onBurger = () => {
        if (window.matchMedia(DESKTOP_QUERY).matches) setNavOpen((open) => !open);
        else setMobileOpen(true);
    };

    const settings = {
        showCompare: flag(content.showCompare, true),
        showWishlist: flag(content.showWishlist, true),
        showLabels: flag(content.showIconLabels, true),
    };
    const trustItems = childNodes(content.trustItems).filter((item) => item.label);
    const promoText = decodeText(content.promoText);
    const showPromo = flag(content.promoEnabled, true) && !!promoText;
    const search = {
        placeholder: decodeText(content.searchPlaceholder),
        questions: splitLines(decodeText(content.searchQuestions)),
        // The root page travels along so a registered search provider can read its own
        // fields from it (the sales demo keeps the ai12z key there).
        page: rootPage,
    };

    const headerClass = [
        "shop-header",
        condensed ? "shop-header--condensed" : "",
        navOpen ? "shop-header--nav-open" : "",
        settings.showLabels ? "shop-header--labels" : "",
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <header className={headerClass}>
            {showPromo && <PromoBar text={promoText} link={content.promoLink} storageKey={`shop-promo-dismissed:${content["@id"] || navigationId}`} />}
            <TrustBar items={trustItems} />

            <div ref={sentinelRef} className="shop-header__sentinel" aria-hidden="true" />
            <div className="shop-header__placeholder" style={{ height: condensed ? barHeight : 0 }} aria-hidden="true" />

            <div ref={barRef} className="shop-header__bar">
                <div className="container">
                    <div className="shop-header__row">
                        <button type="button" className="shop-header__burger" onClick={onBurger} aria-label="Menu" aria-expanded={navOpen || mobileOpen}>
                            <span />
                            <span />
                            <span />
                        </button>
                        <Logo logoClass="shop-header__logo" />
                        <div className="shop-header__search">
                            <SearchBox {...search} />
                        </div>
                        <IconGroup {...settings} />
                    </div>
                </div>

                <div className="shop-header__nav">
                    <div className="container shop-header__nav-inner">
                        <NavMenu content={content} />
                        <div className="shop-header__nav-tools">
                            <LanguageCurrencyChanger phone={content.phone} />
                        </div>
                    </div>
                </div>
            </div>

            <MobileOffCanvas
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                content={content}
                trustItems={trustItems}
                trustTitle={decodeText(content.trustTitle) || "Why shop with us"}
                phone={content.phone}
                email={content.email}
                settings={settings}
            />
        </header>
    );
};

export default connect()(multilanguage(Header));